if (!customElements.get('delivery-countdown')) {
  customElements.define(
    'delivery-countdown',
    class DeliveryCountdown extends HTMLElement {
      constructor() {
        super();
        this.cutoffTime = this.dataset.cutoffTime || '16:00';
        this.deliveryDays = parseInt(this.dataset.deliveryDays) || 1;

        // We will inject the full message into this element, overwriting the static Liquid text
        this.descriptionElement = this.querySelector(
          '.delivery-description-text'
        );

        // We will hide the separate countdown element to avoid duplication,
        // as we will include the timer inside the description text for a smoother sentence.
        const separateTimer = this.querySelector('[data-countdown-timer]');
        if (separateTimer) separateTimer.style.display = 'none';
      }

      connectedCallback() {
        // Clear any existing intervals to prevent duplicates if reconnected
        this.disconnectedCallback();

        this.calculateDeliveryInfo();
        this.updateDisplay();

        // Update every second for the countdown
        this.updateInterval = setInterval(() => {
          this.updateDisplay();
        }, 1000);

        // Check delivery logic every minute (in case we cross the 4pm threshold)
        this.calculateInterval = setInterval(() => {
          this.calculateDeliveryInfo();
        }, 60000);
      }

      disconnectedCallback() {
        // Clear intervals when element is removed from DOM to prevent memory leaks
        if (this.updateInterval) {
          clearInterval(this.updateInterval);
          this.updateInterval = null;
        }
        if (this.calculateInterval) {
          clearInterval(this.calculateInterval);
          this.calculateInterval = null;
        }
      }

      calculateDeliveryInfo() {
        const now = new Date();
        const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon ... 6=Sat
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        const [cutoffHour, cutoffMinute] = this.cutoffTime
          .split(':')
          .map(Number);

        // 1. Determine if we are past the cutoff time today
        const isPastCutoff =
          currentHour > cutoffHour ||
          (currentHour === cutoffHour && currentMinute >= cutoffMinute);

        let deliveryDate = new Date(now);
        let nextCutoffTarget = new Date(now);

        // Set target time to the cutoff hour/minute
        nextCutoffTarget.setHours(cutoffHour, cutoffMinute, 0, 0);

        // --- RULES IMPLEMENTATION ---
        // Mon-Thurs (before 4pm): Delivery tomorrow
        // Mon-Wed (after 4pm): Delivery day after tomorrow
        // Thursday (after 4pm) and Friday (before 4pm): Delivery Monday
        // Friday (after 4pm), Saturday, and Sunday: Delivery Tuesday

        if (dayOfWeek >= 1 && dayOfWeek <= 3) {
          // MONDAY (1), TUESDAY (2), WEDNESDAY (3)
          if (!isPastCutoff) {
            // Before cutoff: Deliver tomorrow
            deliveryDate.setDate(now.getDate() + 1);
            // Target is today's cutoff (already set)
          } else {
            // After cutoff: Deliver day after tomorrow
            deliveryDate.setDate(now.getDate() + 2);
            nextCutoffTarget.setDate(now.getDate() + 1); // Target is tomorrow's cutoff
          }
        } else if (dayOfWeek === 4) {
          // THURSDAY (4)
          if (!isPastCutoff) {
            // Before cutoff: Deliver tomorrow (Friday)
            deliveryDate.setDate(now.getDate() + 1);
            // Target is today's cutoff
          } else {
            // After cutoff: Deliver Monday (4 days later)
            deliveryDate.setDate(now.getDate() + 4);
            // Target is NEXT FRIDAY's cutoff (to order for Monday delivery)
            nextCutoffTarget.setDate(now.getDate() + 1);
          }
        } else if (dayOfWeek === 5) {
          // FRIDAY (5)
          if (!isPastCutoff) {
            // Before cutoff: Deliver Monday (3 days later)
            deliveryDate.setDate(now.getDate() + 3);
            // Target is today's cutoff
          } else {
            // After cutoff: Deliver Tuesday (4 days later)
            deliveryDate.setDate(now.getDate() + 4);
            // Target is NEXT MONDAY's cutoff
            nextCutoffTarget.setDate(now.getDate() + 3);
          }
        } else {
          // SATURDAY (6), SUNDAY (0)
          // Always deliver Tuesday
          const daysUntilTuesday = dayOfWeek === 0 ? 2 : 3; // Sun=2, Sat=3
          deliveryDate.setDate(now.getDate() + daysUntilTuesday);

          // Target is NEXT MONDAY's cutoff
          const daysUntilMonday = dayOfWeek === 0 ? 1 : 2; // Sun=1, Sat=2
          nextCutoffTarget.setDate(now.getDate() + daysUntilMonday);
        }

        this.deliveryDate = deliveryDate;
        this.countdownTarget = nextCutoffTarget;
      }

      formatTimeRemaining() {
        const now = new Date();
        const timeLeft = this.countdownTarget.getTime() - now.getTime();

        if (timeLeft <= 0) {
          // If time hits 0, force a recalculation immediately
          this.calculateDeliveryInfo();
          return 'momentarily';
        }

        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

        // Helper for formatting
        const pad = (n) => (n < 10 ? `0${n}` : n);

        // Logic: If > 24 hours, show "X days Y hours". If < 24 hours, show "XX hrs XX mins XX secs"
        if (days > 0) {
          return `${days} day${days !== 1 ? 's' : ''} and ${hours} hour${
            hours !== 1 ? 's' : ''
          }`;
        }

        // Standard HMS format
        return `${hours} hr ${pad(minutes)} min ${pad(seconds)} sec`;
      }

      updateDisplay() {
        if (!this.descriptionElement) return;

        // 1. Get readable Delivery Date (e.g., "Tuesday 24th")
        const dateString = this.deliveryDate.toLocaleDateString('en-GB', {
          // 'en-GB' ensures Day/Month format
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });

        // 2. Get countdown string
        const timerString = this.formatTimeRemaining();

        // 3. Construct the HTML
        // We overwrite the Liquid text entirely to ensure the sentence structure makes sense
        const html = `
      ${this.descriptionElement.dataset.deliveryCountdownDescription
        .replace('[delivery_date]', dateString)
        .replace('[time_remaining]', timerString)}
    `;

        this.descriptionElement.innerHTML = html;
      }
    }
  );
}
